import React from 'react';
import { debounce, getCurrentOriginUrl } from '../../../helpers/utils';
import {
  getLocationFromStation,
  interlinking,
  isZipCode,
  transformLocation
} from '../../../_integrations/everymundo/busDirectory';
import { useBusDirectoryTracking } from '../../../_integrations/everymundo/tracking/busDirectory';
import { BusDirectoryProps } from '../BusDirectoryMapContainer/BusDirectoryMapContainer';

export enum ActionType {
  STATIONS_REQUEST_START = 'STATIONS_REQUEST_START',
  STATIONS_REQUEST_SUCCESS = 'STATIONS_REQUEST_SUCCESS',
  LOCATIONS_REQUEST_ERROR = 'LOCATIONS_REQUEST_ERROR',
  STATIONS_REQUEST_ERROR = 'STATIONS_REQUEST_ERROR',
  CLEAR_ORIGIN = 'CLEAR_ORIGIN',
  CLEAR_DESTINATION = 'CLEAR_DESTINATION',
  ORIGIN_CHANGE = 'ORIGIN_CHANGE',
  DESTINATION_CHANGE = 'DESTINATION_CHANGE',
  DESTINATION_DATA_CHANGE = 'DESTINATION_DATA_CHANGE',
  ORIGIN_DATA_LOADING = 'ORIGIN_DATA_LOADING',
  DESTINATION_DATA_LOADING = 'DESTINATION_DATA_LOADING',
  ORIGIN_DATA_CHANGE = 'ORIGIN_DATA_CHANGE',
  STATION_SELECT = 'STATION_SELECT',
  ORIGIN_VALIDATION_ERROR = 'ORIGIN_VALIDATION_ERROR',
  DESTINATION_VALIDATION_ERROR = 'DESTINATION_VALIDATION_ERROR',
  ROUTE_VALIDATION_ERROR = 'ROUTE_VALIDATION_ERROR',
  STATION_CHANGE = 'STATION_CHANGE',
  CLEAR_BUS_STATION = 'CLEAR_BUS_STATION',
  LOAD_MORE_STATIONS = 'LOAD_MORE_STATIONS',
  RELOAD_STATIONS = 'RELOAD_STATIONS',
  SEARCH_STATIONS = 'SEARCH_STATIONS',
  ORIGIN_INPUT_CHANGE = 'ORIGIN_INPUT_CHANGE',
  DESTINATION_INPUT_CHANGE = 'DESTINATION_INPUT_CHANGE',
  FARE_CLICK = 'FARE_CLICK',
  ZOOM = 'ZOOM'
}

function dataReducer(state: $TSFixMe, action: $TSFixMe) {
  switch (action.type) {
    case ActionType.STATIONS_REQUEST_START: {
      return {
        ...state,
        isLoading: !state.isSkeletonShown
      };
    }
    case ActionType.STATIONS_REQUEST_SUCCESS: {
      const { busStations, points } =
        action.payload.from === 0
          ? {
              busStations: action.payload.data,
              points: action.payload.points
            }
          : {
              busStations: [...state.busStations, ...action.payload.data],
              points: [...state.points, ...action.payload.points]
            };

      const { flavor } = action.payload;
      let { origin, destination } = action.payload;
      origin = getLocationFromStation(origin, busStations[0], flavor);
      destination = getLocationFromStation(
        destination,
        origin == null ? busStations[0] : busStations[1],
        flavor
      );

      return {
        ...state,
        isSkeletonShown: false,
        errorLoading: false,
        busStations,
        points,
        selectedOrigin: origin,
        selectedDestination: destination,
        origin,
        destination,
        originData: origin ? [origin] : [],
        destinationData: destination ? [destination] : [],
        errorOrigin: false,
        errorDestination: false,
        errorRoute: false,
        isLoading: false,
        showNoData: busStations.length === 0,
        showPagination: true,
        initialLoad: false
      };
    }
    case ActionType.LOAD_MORE_STATIONS: {
      return {
        ...state,
        from: state.from + action.payload.step,
        isLoading: true
      };
    }
    case ActionType.RELOAD_STATIONS: {
      return {
        ...state,
        from: 0,
        query: {
          ...state.query
        }
      };
    }
    case ActionType.SEARCH_STATIONS: {
      return {
        ...state,
        query: {
          origin: action.payload.origin,
          destination: action.payload.destination,
          isDestinationZipSearch: state.isDestinationZipSearch,
          isOriginZipSearch: state.isOriginZipSearch
        },
        defaultOptions: action.payload.defaultOptions,
        from: 0
      };
    }
    case ActionType.STATIONS_REQUEST_ERROR: {
      if (state.initialLoad) {
        return {
          ...state,
          isSkeletonShown: false,
          errorLoading: true,
          isLoading: false,
          initialLoad: false
        };
      }
      return {
        ...state,
        isLoading: false,
        showPagination: false,
        initialLoad: false,
        errorLoading: state.from === 0
      };
    }
    case ActionType.LOCATIONS_REQUEST_ERROR: {
      return {
        ...state,
        isSkeletonShown: false,
        errorLoading: true
      };
    }
    case ActionType.ORIGIN_DATA_LOADING: {
      return {
        ...state,
        isLoadingOrigins: true
      };
    }
    case ActionType.DESTINATION_DATA_LOADING: {
      return {
        ...state,
        isLoadingDestinations: true
      };
    }
    case ActionType.ORIGIN_DATA_CHANGE: {
      if (action.payload.value === state.inputOrigin) {
        return {
          ...state,
          isLoadingOrigins: false,
          originData: action.payload.data,
          isOriginZipSearch: isZipCode(state.inputOrigin)
        };
      }
      return state;
    }
    case ActionType.DESTINATION_DATA_CHANGE: {
      if (action.payload.value === state.inputDestination) {
        return {
          ...state,
          isLoadingDestinations: false,
          destinationData: action.payload.data,
          isDestinationZipSearch: isZipCode(state.destinationOrigin)
        };
      }
      return state;
    }
    case ActionType.ORIGIN_CHANGE: {
      return {
        ...state,
        origin: action.payload,
        errorOrigin: false,
        errorRoute: false
      };
    }
    case ActionType.DESTINATION_CHANGE: {
      return {
        ...state,
        destination: action.payload,
        errorDestination: false,
        errorRoute: false
      };
    }
    case ActionType.CLEAR_ORIGIN: {
      return {
        ...state,
        origin: null,
        originData: [],
        inputOrigin: null
      };
    }
    case ActionType.CLEAR_DESTINATION: {
      return {
        ...state,
        destination: null,
        destinationData: [],
        inputDestination: null
      };
    }
    case ActionType.ORIGIN_VALIDATION_ERROR: {
      return {
        ...state,
        errorOrigin: true
      };
    }
    case ActionType.DESTINATION_VALIDATION_ERROR: {
      return {
        ...state,
        errorDestination: true
      };
    }
    case ActionType.ROUTE_VALIDATION_ERROR: {
      return {
        ...state,
        errorRoute: true
      };
    }
    case ActionType.STATION_CHANGE: {
      return {
        ...state,
        busStationSelected: action.payload
      };
    }
    case ActionType.CLEAR_BUS_STATION: {
      return {
        ...state,
        busStationSelected: null
      };
    }
    case ActionType.ORIGIN_INPUT_CHANGE: {
      return {
        ...state,
        inputOrigin: action.payload.inputSearch,
        originData: action.payload.clearResults ? [] : state.originData
      };
    }
    case ActionType.DESTINATION_INPUT_CHANGE: {
      return {
        ...state,
        inputDestination: action.payload.inputSearch,
        destinationData: action.payload.clearResults ? [] : state.destinationData
      };
    }
    default: {
      return state;
    }
  }
}

export async function loadBusStations({
  integrationSettings,
  pageContext,
  dispatch,
  showOrigin,
  showDestination,
  origin,
  destination,
  isOriginZipSearch,
  isDestinationZipSearch,
  from,
  step,
  interlinkingSettings,
  directionsBaseUrl,
  getBusStations,
  translate,
  language,
  defaultOptions,
  flavor,
  meta,
  getCorrectGeoIdFor,
  getOriginCityInfo,
  getDestinationCityInfo,
  isPrepopulatedActionDone
}: $TSFixMe) {
  const options = [];
  let newOrigin: any = null;
  let newDestination: any = null;
  if ((showOrigin && !origin) || (showDestination && !destination)) {
    dispatch({
      type: ActionType.STATIONS_REQUEST_SUCCESS,
      payload: {
        data: [],
        points: [],
        origin,
        destination,
        from,
        step
      }
    });
  } else {
    if (origin && origin.geoId && origin.searchCode) {
      if (getCorrectGeoIdFor === 'OriginCity' && isPrepopulatedActionDone === false) {
        newOrigin = await getOriginCityInfo();
        options.push({
          defaultOptions,
          language,
          from,
          step,
          location: newOrigin,
          isZipSearch: isOriginZipSearch
        });
      } else {
        options.push({
          defaultOptions,
          language,
          from,
          step,
          location: origin,
          isZipSearch: isOriginZipSearch
        });
      }
    }
    if (destination && destination.geoId && destination.searchCode) {
      if (getCorrectGeoIdFor === 'DestinationCity' && isPrepopulatedActionDone === false) {
        newDestination = await getDestinationCityInfo();
        options.push({
          defaultOptions,
          language,
          from,
          step,
          location: newDestination,
          isZipSearch: isDestinationZipSearch
        });
      } else {
        options.push({
          defaultOptions,
          language,
          from,
          step,
          location: destination,
          isZipSearch: isDestinationZipSearch
        });
      }
    }
    dispatch({ type: ActionType.STATIONS_REQUEST_START });
    const enableRoute = showOrigin && showDestination;
    getBusStations({
      integrationSettings,
      pageContext,
      options,
      enableRoute,
      interlinkingSettings,
      translate,
      directionsBaseUrl,
      meta
    })
      .then((payload: $TSFixMe) => {
        dispatch({
          type: ActionType.STATIONS_REQUEST_SUCCESS,
          payload: {
            ...payload,
            origin: newOrigin !== null ? newOrigin : origin,
            destination: newDestination !== null ? newDestination : destination,
            from,
            step,
            flavor,
            ...(newOrigin !== null && {
              originData: [newOrigin]
            }),
            ...(newDestination !== null && {
              destinationData: [newDestination]
            })
          }
        });
      })
      .catch(() => {
        dispatch({
          type: ActionType.STATIONS_REQUEST_ERROR
        });
      });
  }
}

export function useBusDirectory({
  initialBusStations,
  integrationSettings,
  prePopulation,
  pageContext,
  meta,
  uiDisplay: { showOrigin, showDestination },
  paginationSettings,
  interlinkingBaseUrl: defaultInterlinkingUrl,
  preferInterlinkingBaseUrl,
  directionsBaseUrl,
  translate,
  getBusStations,
  getLocations,
  flavor,
  ...rest
}: Pick<
  BusDirectoryProps,
  | 'initialBusStations'
  | 'integrationSettings'
  | 'prePopulation'
  | 'pageContext'
  | 'meta'
  | 'uiDisplay'
  | 'paginationSettings'
  | 'interlinkingBaseUrl'
  | 'preferInterlinkingBaseUrl'
  | 'directionsBaseUrl'
  | 'getBusStations'
  | 'getLocations'
  | 'flavor'
  | 'translate'
>) {
  const getCorrectGeoIdFor = (rest as any)?.getCorrectGeoIdFor;
  const [isPrepopulatedActionDone, setIsPrepopulatedActionDone] = React.useState<boolean>(false);

  const getCorrectGeoId = (
    prePopulationOrigin: any,
    prePopulationDestination: any,
    category: string
  ) => {
    return new Promise((resolve, reject) => {
      let query = '';
      let label = '';
      if (category === 'OriginCity' && prePopulationOrigin && prePopulationOrigin?.originCityName) {
        query = prePopulationOrigin?.originCityName;
        label = `${prePopulationOrigin?.originCityName}, ${prePopulationOrigin?.originStateName}`;
      } else if (
        category === 'DestinationCity' &&
        prePopulationDestination &&
        prePopulationDestination?.destinationCityName
      ) {
        query = prePopulationDestination?.destinationCityName;
        label = `${prePopulationDestination?.destinationCityName}, ${prePopulationDestination?.destinationStateName}`;
      }
      if (query !== '') {
        getLocations({
          integrationSettings,
          pageContext,
          meta,
          options: {
            ...prePopulation.locationSearchOptions,
            language: prePopulation.languageCode,
            query
          }
        }).then((res: any) => {
          let dataIndex = -1;
          for (let index = 0; index < res.length; index++) {
            const item = res[index];
            if (item?.label === label) {
              dataIndex = index;
              break;
            }
          }
          if (dataIndex !== -1) {
            resolve(res[dataIndex]);
          } else {
            resolve(null);
          }
        });
      } else {
        reject(
          new Error(
            'originCityName|destinationCityName and originStateName|destinationStateName settings are required'
          )
        );
      }
    });
  };

  async function getOriginCityInfo() {
    if (prePopulation?.origin && getCorrectGeoIdFor === 'OriginCity') {
      const originCityData: any = await getCorrectGeoId(
        prePopulation?.origin,
        null,
        getCorrectGeoIdFor
      );
      setIsPrepopulatedActionDone(true);
      let newOriginCityData;
      if (originCityData !== null) {
        newOriginCityData = {
          ...transformLocation(prePopulation?.origin),
          proximityGeoId: originCityData?.value,
          label: originCityData?.label,
          name: originCityData?.label
        };
      } else {
        newOriginCityData = {
          ...transformLocation(prePopulation?.origin)
        };
      }
      return newOriginCityData;
    }
    return prePopulation.origin && transformLocation(prePopulation.origin);
  }

  async function getDestinationCityInfo() {
    if (prePopulation?.destination && getCorrectGeoIdFor === 'DestinationCity') {
      const destinationCityData: any = await getCorrectGeoId(
        null,
        prePopulation?.destination,
        getCorrectGeoIdFor
      );
      setIsPrepopulatedActionDone(true);
      let newDestinationCityData;
      if (destinationCityData !== null) {
        newDestinationCityData = {
          ...transformLocation(prePopulation?.destination),
          proximityGeoId: destinationCityData?.value,
          label: destinationCityData?.label,
          name: destinationCityData?.label
        };
      } else {
        newDestinationCityData = {
          ...transformLocation(prePopulation?.destination)
        };
      }
      return newDestinationCityData;
    }
    return prePopulation.destination && transformLocation(prePopulation.destination);
  }

  const { limit = 30, step, initialVisibleItems } = paginationSettings;
  const interlinkingBaseUrl = preferInterlinkingBaseUrl
    ? defaultInterlinkingUrl
    : getCurrentOriginUrl(defaultInterlinkingUrl);
  const defaultOrigin = prePopulation.origin && transformLocation(prePopulation.origin);
  const defaultDestination =
    prePopulation.destination && transformLocation(prePopulation.destination);
  const [state, stateDispatch] = React.useReducer(dataReducer, {
    isSkeletonShown: true,
    showNoData: false,
    busStations: initialBusStations || [],
    busStationSelected: null,
    errorLoading: false,
    query: {
      origin: defaultOrigin,
      destination: defaultDestination
    },
    defaultOptions: { default: prePopulation.busSearchOptions },
    isOriginZipSearch: false,
    isDestinationZipSearch: false,
    initialLoad: true,
    origin: defaultOrigin,
    destination: defaultDestination,
    inputOrigin: undefined,
    inputDestination: undefined,
    originData: defaultOrigin ? [defaultOrigin] : [],
    destinationData: defaultDestination ? [defaultDestination] : [],
    selectedOrigin: defaultOrigin,
    selectedDestination: defaultDestination,
    from: 0,
    isLoadingOrigins: false,
    isLoadingDestinations: false,
    isLoading: false,
    errorOrigin: false,
    errorDestination: false,
    errorRoute: false,
    currentComponent: '',
    points: [],
    showPagination: true
  });

  const track = useBusDirectoryTracking();

  function dispatch(action: $TSFixMe) {
    stateDispatch(action);
    track(state, action);
  }

  const {
    origin,
    destination,
    from,
    busStations,
    busStationSelected,
    showPagination,
    query,
    inputOrigin,
    inputDestination,
    defaultOptions,
    isLoading
  } = state;

  const originSelectorRef = React.useRef<$TSFixMe>(null);
  const destinationSelectorRef = React.useRef<$TSFixMe>(null);

  React.useEffect(() => {
    loadBusStations({
      ...query,
      integrationSettings,
      pageContext,
      meta,
      dispatch,
      showOrigin,
      showDestination,
      from,
      step: from === 0 ? initialVisibleItems : step,
      getBusStations,
      directionsBaseUrl,
      getCorrectGeoIdFor,
      getDestinationCityInfo,
      getOriginCityInfo,
      isPrepopulatedActionDone,
      interlinkingSettings: {
        baseUrl: interlinkingBaseUrl,
        siteEdition: prePopulation.siteEdition,
        pageType: pageContext.pageType,
        showOrigin,
        showDestination,
        filter: prePopulation.locationSearchOptions.outputCategory
      },
      flavor,
      language: prePopulation.languageCode,
      defaultOptions,
      translate
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    from,
    getBusStations,
    initialVisibleItems,
    integrationSettings,
    interlinkingBaseUrl,
    pageContext.pageType,
    prePopulation,
    query,
    showDestination,
    showOrigin,
    step,
    translate,
    flavor,
    defaultOptions,
    directionsBaseUrl
  ]);

  const loadLocations = React.useCallback(
    (locationQuery, type) => {
      dispatch({
        type:
          type === 'origin' ? ActionType.ORIGIN_DATA_LOADING : ActionType.DESTINATION_DATA_LOADING
      });
      getLocations({
        integrationSettings,
        pageContext,
        meta,
        options: {
          ...prePopulation.locationSearchOptions,
          language: prePopulation.languageCode,
          query: locationQuery.toLowerCase()
        }
      }).then((payload: $TSFixMe) => {
        dispatch({
          type:
            type === 'origin' ? ActionType.ORIGIN_DATA_CHANGE : ActionType.DESTINATION_DATA_CHANGE,
          payload: {
            data: payload,
            value: locationQuery
          }
        });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getLocations, integrationSettings, prePopulation]
  );

  React.useEffect(() => {
    if (
      inputOrigin &&
      inputOrigin.length >= prePopulation.minSearchLength &&
      inputOrigin.length <= prePopulation.maxSearchLength
    ) {
      loadLocations(inputOrigin, 'origin');
    }
  }, [inputOrigin, loadLocations, prePopulation]);

  React.useEffect(() => {
    if (
      inputDestination &&
      inputDestination.length >= prePopulation.minSearchLength &&
      inputDestination.length <= prePopulation.maxSearchLength
    ) {
      loadLocations(inputDestination, 'destination');
    }
  }, [inputDestination, loadLocations, prePopulation]);

  const handleOnInputChange = debounce((inputSearch: $TSFixMe, type: $TSFixMe) => {
    dispatch({
      type:
        type === 'origin' ? ActionType.ORIGIN_INPUT_CHANGE : ActionType.DESTINATION_INPUT_CHANGE,
      payload: {
        inputSearch,
        clearResults: inputSearch.length < prePopulation.minSearchLength
      }
    });
  }, 300);

  function clearOrigin() {
    originSelectorRef.current?.focus();
    dispatch({ type: ActionType.CLEAR_ORIGIN });
  }

  function handleLocation(value: $TSFixMe, type: $TSFixMe) {
    const newValue = {
      ...value
    };
    delete newValue.value;
    dispatch({
      type: type === 'origin' ? ActionType.ORIGIN_CHANGE : ActionType.DESTINATION_CHANGE,
      payload: newValue
    });
  }

  function handleOrigin(value: $TSFixMe) {
    handleLocation(value, 'origin');
  }

  function handleDestination(value: $TSFixMe) {
    handleLocation(value, 'destination');
  }

  function isPaginationAvailable() {
    const itemCount = busStations.length;
    const requestedItems = from === 0 ? initialVisibleItems : from + step;
    return (
      showPagination &&
      busStations &&
      ((itemCount < limit && itemCount >= requestedItems) || isLoading)
    );
  }

  function handlePaginationAction() {
    if (isPaginationAvailable()) {
      dispatch({
        type: ActionType.LOAD_MORE_STATIONS,
        payload: { step: from === 0 ? initialVisibleItems : step }
      });
    }
  }

  function reload() {
    dispatch({ type: ActionType.RELOAD_STATIONS });
  }

  function handleSearch() {
    if (showOrigin && !origin) {
      originSelectorRef.current.focus();
      dispatch({ type: ActionType.ORIGIN_VALIDATION_ERROR });
    } else if (showDestination && !destination) {
      destinationSelectorRef.current.focus();
      dispatch({ type: ActionType.ORIGIN_VALIDATION_ERROR });
    } else if (showOrigin && showDestination && origin.geoId === destination.geoId) {
      destinationSelectorRef.current.focus();
      dispatch({ type: ActionType.ROUTE_VALIDATION_ERROR });
    } else {
      dispatch({
        type: ActionType.SEARCH_STATIONS,
        payload: {
          origin,
          destination,
          defaultOptions: {
            ...prePopulation.busSearchOptionsOnInteractive,
            default: {
              ...prePopulation.busSearchOptions,
              ...(prePopulation.busSearchOptionsOnInteractive &&
                prePopulation.busSearchOptionsOnInteractive.default)
            }
          }
        }
      });
    }
  }

  function clearDestination() {
    destinationSelectorRef.current.focus();
    dispatch({ type: ActionType.CLEAR_DESTINATION });
  }

  function handleMarkerOnClick(station: $TSFixMe) {
    dispatch({ type: ActionType.STATION_CHANGE, payload: station });
  }

  function handleTooltipOnClose() {
    dispatch({ type: ActionType.CLEAR_BUS_STATION });
  }

  function handleStationItemFocus(data: $TSFixMe) {
    if (busStationSelected && busStationSelected.index !== data.index) {
      dispatch({ type: ActionType.CLEAR_BUS_STATION });
    }
  }

  function getInterlinking() {
    return interlinking({
      baseUrl: interlinkingBaseUrl,
      siteEdition: prePopulation.siteEdition,
      showOrigin,
      showDestination,
      busStations,
      filter: prePopulation.locationSearchOptions.outputCategory
    });
  }

  const listItem = React.useRef<$TSFixMe>(null);

  React.useEffect(() => {
    if (listItem.current) {
      listItem.current.focus();
    }
  }, [busStationSelected]);

  return {
    ...state,
    enableRoute: showOrigin && showDestination,
    handleOnInputChange,
    clearOrigin,
    handleOrigin,
    handleDestination,
    isPaginationAvailable,
    handlePaginationAction,
    reload,
    clearDestination,
    handleSearch,
    originSelectorRef,
    destinationSelectorRef,
    listItem,
    handleMarkerOnClick,
    handleTooltipOnClose,
    handleStationItemFocus,
    getInterlinking,
    dispatch
  };
}
